<!--
================================================================================
  BaseRadio.vue – Universal Reusable Radio Button (Single OR Group)
================================================================================

  FEATURES:
  • Works as single radio OR group
  • Same `name` + different `value` → radio group
  • Shared `v-model` (selected value)
  • Vee-Validate + error/success styling
  • Inline / block layout
  • Icons + disabled
  • Auto-detects mode

================================================================================
  USAGE EXAMPLES
================================================================================

  1. SINGLE RADIO (your original use-case)
     <BaseRadio
       name="r_rule_count"
       v-model="reoccurrence.r_rule_count"
       inputId="indefinite"
       :value="0"
       label="Indefinite"
     />

     <BaseRadio
       name="r_rule_count"
       v-model="reoccurrence.r_rule_count"
       inputId="specificCount"
       :value="1"
       label="Specific Count"
     />

  2. GROUP (multiple options)
     <BaseRadio
       v-model="form.status"
       name="status"
       :options="[
         { label: 'Active', value: 'active' },
         { label: 'Inactive', value: 'inactive' }
       ]"
       :inline="true"
     />

  3. 3 SECTIONS → DIFFERENT VALUES
     <BaseRadio
       v-model="form.recurrence"
       name="recurrence"
       :options="recurrenceOptions"
       section="1"
     />

================================================================================
-->

<template>
  <div
    class="base-radio"
    :class="{
      'has-error': !!errorMessage,
      'has-success': fieldMeta.valid && fieldMeta.touched,
    }"
  >
    <!-- SINGLE MODE -->
    <div v-if="isSingle" class="flex align-items-center">
      <RadioButton
        :id="inputId"
        v-model="proxyValue"
        :name="name"
        :value="value"
        :disabled="disabled"
        :class="{ 'p-invalid': !!errorMessage }"
        @change="emitChange"
      />
      <label
        v-if="label"
        :for="inputId"
        class="ml-2 cursor-pointer"
        :class="{ 'opacity-60': disabled }"
      >
        <i v-if="icon" class="pi mr-1" :class="icon" />
        {{ label }}
      </label>
    </div>

    <!-- GROUP MODE -->
    <div v-else :class="inline ? 'inline-group' : 'block-group'">
      <div
        v-for="(opt, index) in resolvedOptions"
        :key="opt.key"
        :class="inline ? 'inline-item' : 'block-item'"
      >
        <RadioButton
          :id="`${inputId}-${index}`"
          v-model="proxyValue"
          :name="opt.name || name"
          :value="opt.sectionValue ?? opt.value"
          :disabled="disabled"
          :class="{ 'p-invalid': !!errorMessage }"
          @change="emitChange"
        />
        <label
          :for="`${inputId}-${index}`"
          class="ml-2 cursor-pointer"
          :class="{ 'opacity-60': disabled }"
        >
          <i v-if="opt.icon" class="pi mr-1" :class="opt.icon" />
          {{ opt.label }}
        </label>
      </div>
    </div>

    <!-- Error message -->
    <small v-if="errorMessage" class="p-error block mt-1">
      {{ errorMessage }}
    </small>
  </div>
</template>

<script setup>
import { computed, watch } from "vue";
import { useField } from "vee-validate";
import RadioButton from "primevue/radiobutton";

const props = defineProps({
  modelValue: { type: [String, Number, Boolean, Object], default: null },

  name: { type: String, required: true },
  inputId: { type: String, default: "" },

  // Single mode
  value: { type: [String, Number, Boolean, Object], default: null },
  label: { type: String, default: "" },
  icon: { type: String, default: "" },

  // Group mode
  options: { type: Array, default: () => [] },
  weekDays: { type: Array, default: () => [] },
  section: { type: String, default: "" },

  disabled: { type: Boolean, default: false },
  inline: { type: Boolean, default: false },
  errorMessage: { type: String, default: "" },
});

const emit = defineEmits(["update:modelValue", "change"]);

/* ------------------------------------------------------------------ */
/* Mode Detection: Single vs Group                                    */
/* ------------------------------------------------------------------ */
const isSingle = computed(() => {
  return props.value !== null && props.options.length === 0;
});

/* ------------------------------------------------------------------ */
/* Unique ID                                                          */
/* ------------------------------------------------------------------ */
const inputId = computed(() => {
  if (props.inputId) return props.inputId;
  return `${props.name}-radio`;
});

/* ------------------------------------------------------------------ */
/* Resolve Options (Group Mode)                                       */
/* ------------------------------------------------------------------ */
const resolvedOptions = computed(() => {
  if (props.options.length > 0) {
    return props.options.map((opt, i) => ({
      key: opt.key ?? i,
      label: opt.label,
      value: opt.value,
      sectionValue: opt.sectionValue?.[props.section],
      name: opt.name,
      icon: opt.icon,
    }));
  }

  return props.weekDays.map((day, i) => ({
    key: i,
    label: day,
    value: i,
    name: props.name,
  }));
});

/* ------------------------------------------------------------------ */
/* Vee-Validate                                                       */
/* ------------------------------------------------------------------ */
let field = null;
let fieldMeta = { valid: false, touched: false };
let veeError = "";

if (props.name) {
  const {
    value: fieldValue,
    errorMessage: veeMsg,
    handleChange,
    meta,
  } = useField(props.name, undefined, {
    type: "radio",
    initialValue: props.modelValue,
  });

  field = { fieldValue, handleChange };
  fieldMeta = meta;
  veeError = veeMsg.value;

  watch(
    () => props.modelValue,
    (newVal) => {
      if (newVal !== fieldValue.value) {
        fieldValue.value = newVal;
        handleChange(newVal);
      }
    },
  );
}

/* ------------------------------------------------------------------ */
/* Proxy v-model                                                      */
/* ------------------------------------------------------------------ */
const proxyValue = computed({
  get() {
    return field ? field.fieldValue.value : props.modelValue;
  },
  set(val) {
    if (field) {
      field.fieldValue.value = val;
      field.handleChange(val);
    }
    emit("update:modelValue", val);
  },
});

/* ------------------------------------------------------------------ */
/* Error message                                                      */
/* ------------------------------------------------------------------ */
const errorMessage = computed(() => props.errorMessage || veeError);

const emitChange = (e) => emit("change", e);
</script>

<style scoped>
.base-radio {
  margin-bottom: 0rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Layout */
.inline-group {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
}
.inline-item {
  display: flex;
  align-items: center;
}
.block-group {
  display: flex;
  flex-direction: column;
}
.block-item {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

/* Styling */
.base-radio.has-error :deep(.p-radiobutton-box) {
  border-color: var(--red-500);
}
.base-radio.has-success :deep(.p-radiobutton-box) {
  border-color: var(--green-500);
}

.cursor-pointer {
  cursor: pointer;
  user-select: none;
}
.opacity-60 {
  opacity: 0.6;
}
</style>
